import React, { useState } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { 
  Network as NetworkIcon, Plus, Search, Calendar, AlertTriangle, 
  CheckCircle, Clock, Eye, Users, Target,
  TrendingUp, FileText, Download, Printer,
  RefreshCw, Bell, Activity, Wifi,
  Globe, Shield, Settings, Database,
  MessageSquare, Heart, Phone, Video, Camera,
  Send, Lock, UserPlus, UserCheck, Star,
  MapPin, Mail, Smartphone, Tablet,
  Brain, Cpu, Sparkles, Zap, BarChart3,
  TrendingUp as TrendingUpIcon
} from 'lucide-react';

const Network = () => {
  const { state } = useAppContext();
  const { deviceInfo } = state;
  const { optimization } = deviceInfo;

  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showVideoCallModal, setShowVideoCallModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // AI Network Optimization Data
  const aiNetworkData = {
    optimization: {
      performance: 94.5,
      security: 98.2,
      reliability: 99.8,
      efficiency: 91.3
    },
    predictions: {
      performanceIssues: 2,
      securityThreats: 1,
      maintenanceNeeds: 3,
      optimizationOpportunities: 5
    },
    recommendations: [
      {
        id: 'NET_AI001',
        type: 'Performance',
        priority: 'High',
        title: 'Optimize network bandwidth allocation',
        description: 'AI detected 20% performance improvement potential through bandwidth optimization.',
        impact: 'Improve network speed by 25%',
        facilities: ['Golden Years Home'],
        status: 'Pending',
        aiConfidence: 93
      },
      {
        id: 'NET_AI002',
        type: 'Security',
        priority: 'Medium',
        title: 'Implement advanced threat detection',
        description: 'AI analysis shows 15% security enhancement with advanced threat detection.',
        impact: 'Reduce security incidents by 30%',
        facilities: ['All Facilities'],
        status: 'Recommended',
        aiConfidence: 89
      },
      {
        id: 'NET_AI003',
        type: 'Efficiency',
        priority: 'Low',
        title: 'Optimize data transfer protocols',
        description: 'AI suggests protocol optimization for 10% efficiency improvement.',
        impact: 'Reduce data transfer time by 15%',
        facilities: ['All Facilities'],
        status: 'Suggested',
        aiConfidence: 82
      }
    ],
    analytics: {
      networkHealth: {
        excellent: 2,
        good: 1,
        needsImprovement: 0
      },
      securityLevel: {
        high: 2,
        medium: 1,
        low: 0
      },
      performanceTrends: {
        improving: 2,
        stable: 1,
        declining: 0
      }
    }
  };

  // Mock network data
  const networkStats = {
    totalFacilities: 3,
    connectedDevices: 156,
    activeUsers: 89,
    dataTransfer: '2.4 GB',
    uptime: 99.8,
    securityAlerts: 2,
    maintenance: 1,
    performance: 94.5
  };

  const facilities = [
    {
      id: 'FAC001',
      name: 'Sunshine Gardens',
      location: 'Melbourne, VIC',
      status: 'Online',
      devices: 45,
      users: 28,
      lastSync: '2025-07-24 14:30',
      uptime: 99.9,
      alerts: 0,
      connection: 'High Speed',
      ipAddress: '192.168.1.100',
      dataUsage: '856 MB'
    },
    {
      id: 'FAC002',
      name: 'Bayview Aged Care',
      location: 'Sydney, NSW',
      status: 'Online',
      devices: 38,
      users: 22,
      lastSync: '2025-07-24 14:25',
      uptime: 99.7,
      alerts: 1,
      connection: 'High Speed',
      ipAddress: '192.168.1.101',
      dataUsage: '723 MB'
    },
    {
      id: 'FAC003',
      name: 'Golden Years Home',
      location: 'Brisbane, QLD',
      status: 'Maintenance',
      devices: 32,
      users: 19,
      lastSync: '2025-07-24 13:45',
      uptime: 98.2,
      alerts: 2,
      connection: 'Standard',
      ipAddress: '192.168.1.102',
      dataUsage: '645 MB'
    }
  ];

  const networkAlerts = [
    {
      id: 'ALT001',
      facility: 'Bayview Aged Care',
      type: 'Security',
      severity: 'Medium',
      message: 'Unusual login attempt detected',
      time: '2025-07-24 14:15',
      status: 'Investigating'
    },
    {
      id: 'ALT002',
      facility: 'Golden Years Home',
      type: 'Performance',
      severity: 'Low',
      message: 'Slow network response time',
      time: '2025-07-24 13:30',
      status: 'Resolved'
    },
    {
      id: 'ALT003',
      facility: 'Golden Years Home',
      type: 'Maintenance',
      severity: 'High',
      message: 'Scheduled maintenance in progress',
      time: '2025-07-24 12:00',
      status: 'In Progress'
    }
  ];

  // Family Connect Data
  const familyConnectData = {
    totalFamilies: 127,
    activeConnections: 89,
    messagesToday: 156,
    videoCallsToday: 23,
    satisfaction: 4.8
  };

  const familyMembers = [
    {
      id: 'FM001',
      name: 'Sarah Johnson',
      relationship: 'Daughter',
      resident: 'Margaret Johnson',
      facility: 'Sunshine Gardens',
      status: 'Online',
      lastActive: '2 minutes ago',
      messages: 12,
      videoCalls: 3,
      photos: 8,
      notifications: 2,
      avatar: '👩',
      preferences: {
        notifications: true,
        videoCalls: true,
        photos: true,
        updates: true
      }
    },
    {
      id: 'FM002',
      name: 'Michael Wilson',
      relationship: 'Son',
      resident: 'Robert Wilson',
      facility: 'Bayview Aged Care',
      status: 'Away',
      lastActive: '1 hour ago',
      messages: 5,
      videoCalls: 1,
      photos: 3,
      notifications: 0,
      avatar: '👨',
      preferences: {
        notifications: true,
        videoCalls: false,
        photos: true,
        updates: false
      }
    },
    {
      id: 'FM003',
      name: 'Emma Davis',
      relationship: 'Granddaughter',
      resident: 'Eleanor Davis',
      facility: 'Golden Years Home',
      status: 'Online',
      lastActive: '5 minutes ago',
      messages: 8,
      videoCalls: 2,
      photos: 12,
      notifications: 1,
      avatar: '👧',
      preferences: {
        notifications: true,
        videoCalls: true,
        photos: true,
        updates: true
      }
    }
  ];

  const recentMessages = [
    {
      id: 'MSG001',
      from: 'Sarah Johnson',
      to: 'Margaret Johnson',
      message: 'Hi Mom! How are you feeling today? I hope you had a good night\'s sleep.',
      time: '2025-01-24 14:30',
      type: 'text',
      status: 'read'
    },
    {
      id: 'MSG002',
      from: 'Emma Davis',
      to: 'Eleanor Davis',
      message: 'Grandma, I sent you some new photos from our family dinner last weekend!',
      time: '2025-01-24 13:45',
      type: 'photo',
      status: 'delivered'
    },
    {
      id: 'MSG003',
      from: 'Michael Wilson',
      to: 'Robert Wilson',
      message: 'Dad, the doctor called and said your test results look great!',
      time: '2025-01-24 12:20',
      type: 'text',
      status: 'read'
    }
  ];

  const showNotification = (message, type = 'info') => {
    const id = `${Date.now()}-${Math.random()}`;
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Online': return 'bg-green-100 text-green-800';
      case 'Offline': return 'bg-red-100 text-red-800';
      case 'Maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'Investigating': return 'bg-orange-100 text-orange-800';
      case 'Resolved': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-100 text-red-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredFacilities = facilities.filter(facility => {
    const matchesSearch = facility.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         facility.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || facility.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredAlerts = networkAlerts.filter(alert => {
    const matchesSearch = alert.facility.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         alert.message.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || alert.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-6 text-white shadow-xl min-h-[120px] flex items-center">
        <div className="flex justify-between items-center w-full">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              Network Management
              <NetworkIcon className="w-8 h-8 text-cyan-300 animate-pulse" />
            </h1>
            <p className="text-base mt-2 opacity-90">
              Monitor and manage facility network connections
            </p>
          </div>
          <button className={`bg-white text-cyan-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}>
            <Plus className={`${optimization.iconSize}`} />
            Add Facility
          </button>
        </div>
      </div>

      {/* Network Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{networkStats.totalFacilities}</h3>
              <p className="text-sm text-gray-600">Connected Facilities</p>
              <div className="flex items-center gap-1 text-xs">
                <CheckCircle className="w-3 h-3 text-green-600" />
                <span className="text-green-600">All Online</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Wifi className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{networkStats.connectedDevices}</h3>
              <p className="text-sm text-gray-600">Connected Devices</p>
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600">+12 today</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{networkStats.activeUsers}</h3>
              <p className="text-sm text-gray-600">Active Users</p>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-purple-600">Real-time</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{networkStats.securityAlerts}</h3>
              <p className="text-sm text-gray-600">Security Alerts</p>
              <div className="flex items-center gap-1 text-xs">
                <span className="text-orange-600">Monitoring</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: NetworkIcon },
              { id: 'family-connect', label: 'Family Connect', icon: Heart },
              { id: 'facilities', label: 'Facilities', icon: Globe },
              { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
              { id: 'performance', label: 'Performance', icon: Activity },
              { id: 'ai', label: 'AI Insights', icon: Brain }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-cyan-500 text-cyan-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search facilities, alerts, devices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Investigating">Investigating</option>
                <option value="Resolved">Resolved</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <Calendar className="w-4 h-4" />
                Date Range
              </button>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'family-connect' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-pink-50 to-rose-100 p-6 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-pink-800">Family Connect - Secure Communication</h3>
                  <button 
                    onClick={() => setShowInviteModal(true)}
                    className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    Invite Family
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-pink-800 mb-2">Total Families</h4>
                    <div className="text-2xl font-bold text-pink-600">{familyConnectData.totalFamilies}</div>
                    <div className="text-sm text-gray-600">Connected</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-pink-800 mb-2">Active Now</h4>
                    <div className="text-2xl font-bold text-green-600">{familyConnectData.activeConnections}</div>
                    <div className="text-sm text-gray-600">Online</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-pink-800 mb-2">Messages Today</h4>
                    <div className="text-2xl font-bold text-blue-600">{familyConnectData.messagesToday}</div>
                    <div className="text-sm text-gray-600">Sent</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-pink-800 mb-2">Video Calls</h4>
                    <div className="text-2xl font-bold text-purple-600">{familyConnectData.videoCallsToday}</div>
                    <div className="text-sm text-gray-600">Today</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <h4 className="font-semibold text-pink-800 mb-2">Satisfaction</h4>
                    <div className="text-2xl font-bold text-yellow-600">{familyConnectData.satisfaction}/5</div>
                    <div className="text-sm text-gray-600">Rating</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Family Members */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">Family Members</h4>
                      <button 
                        onClick={() => setShowInviteModal(true)}
                        className="text-pink-600 hover:text-pink-700 text-sm font-medium"
                      >
                        + Add Member
                      </button>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {familyMembers.map((member) => (
                      <div key={member.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">{member.avatar}</div>
                            <div>
                              <h5 className="font-semibold">{member.name}</h5>
                              <p className="text-sm text-gray-600">{member.relationship} of {member.resident}</p>
                              <p className="text-xs text-gray-500">{member.facility}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              member.status === 'Online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {member.status}
                            </span>
                            {member.notifications > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                {member.notifications}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              {member.messages}
                            </span>
                            <span className="flex items-center gap-1">
                              <Video className="w-4 h-4" />
                              {member.videoCalls}
                            </span>
                            <span className="flex items-center gap-1">
                              <Camera className="w-4 h-4" />
                              {member.photos}
                            </span>
                          </div>
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => setShowNewMessageModal(true)}
                              className="p-2 text-gray-600 hover:text-pink-600 hover:bg-pink-50 rounded-lg"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => setShowVideoCallModal(true)}
                              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            >
                              <Video className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                              <Phone className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 mt-2">
                          Last active: {member.lastActive}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Messages */}
                <div className="bg-white rounded-xl border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">Recent Messages</h4>
                      <button 
                        onClick={() => setShowNewMessageModal(true)}
                        className="text-pink-600 hover:text-pink-700 text-sm font-medium"
                      >
                        + New Message
                      </button>
                    </div>
                  </div>
                  <div className="p-4 space-y-4">
                    {recentMessages.map((message) => (
                      <div key={message.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-semibold">{message.from}</h5>
                              <span className="text-xs text-gray-500">to {message.to}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{message.message}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {message.time}
                              </span>
                              <span className="flex items-center gap-1">
                                {message.type === 'photo' ? <Camera className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                                {message.type}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                message.status === 'read' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {message.status}
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                              <Send className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-xl border border-pink-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-pink-800">Send Message</h4>
                      <p className="text-sm text-pink-600">Secure family messaging</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowNewMessageModal(true)}
                    className="w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white py-2 px-4 rounded-lg hover:from-pink-600 hover:to-pink-700 transition-all duration-300"
                  >
                    Start Chat
                  </button>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                      <Video className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-800">Video Call</h4>
                      <p className="text-sm text-blue-600">Face-to-face communication</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowVideoCallModal(true)}
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 px-4 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Start Call
                  </button>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-800">Share Photos</h4>
                      <p className="text-sm text-green-600">Send family memories</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => showNotification('Photo sharing feature coming soon!', 'info')}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300"
                  >
                    Share Photos
                  </button>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-800">Invite Family</h4>
                      <p className="text-sm text-purple-600">Add new family members</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setShowInviteModal(true)}
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-2 px-4 rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300"
                  >
                    Invite Family
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Enhanced Network Performance Dashboard */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-100 p-8 rounded-2xl border border-cyan-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-cyan-800 mb-2">Network Performance Dashboard</h3>
                    <p className="text-cyan-600">Real-time network monitoring and analytics</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">All Systems Operational</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-cyan-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{networkStats.uptime}%</div>
                        <div className="text-sm text-gray-600">Uptime</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Last 24h</span>
                        <span className="text-green-600">+0.2%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${networkStats.uptime}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-cyan-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600">{networkStats.performance}%</div>
                        <div className="text-sm text-gray-600">Performance</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Bandwidth</span>
                        <span className="text-blue-600">78% used</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full" style={{width: '78%'}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-cyan-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                        <Database className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">{networkStats.dataTransfer}</div>
                        <div className="text-sm text-gray-600">Data Transfer</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Today</span>
                        <span className="text-purple-600">+12% vs yesterday</span>
                      </div>
                      <div className="text-xs text-gray-500">Peak: 3.2 GB at 2:30 PM</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Network Health Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-800">Network Health</h4>
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                        Excellent
                      </span>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Firewall Protection</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">Active</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">VPN Connections</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">24 Active</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm font-medium">Security Score</span>
                      </div>
                      <span className="text-sm text-yellow-600 font-medium">94/100</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">SSL Certificates</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">Valid</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-800">Recent Alerts</h4>
                      <button className="text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                        View All
                      </button>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {networkAlerts.slice(0, 3).map((alert) => (
                      <div key={alert.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          alert.severity === 'High' ? 'bg-red-500' :
                          alert.severity === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                        }`}></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-medium text-sm">{alert.facility}</h5>
                            <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(alert.severity)}`}>
                              {alert.severity}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{alert.message}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {alert.time}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(alert.status)}`}>
                              {alert.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200 hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-blue-800">Network Map</h5>
                      <p className="text-xs text-blue-600">View topology</p>
                    </div>
                  </div>
                  <div className="text-sm text-blue-700">Visualize network connections and infrastructure</div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200 hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-green-800">Security Center</h5>
                      <p className="text-xs text-green-600">Monitor threats</p>
                    </div>
                  </div>
                  <div className="text-sm text-green-700">Real-time security monitoring and threat detection</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200 hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-purple-800">Performance</h5>
                      <p className="text-xs text-purple-600">Analytics</p>
                    </div>
                  </div>
                  <div className="text-sm text-purple-700">Detailed performance metrics and optimization</div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200 hover:shadow-md transition-shadow cursor-pointer">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                      <Bell className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-orange-800">Alerts</h5>
                      <p className="text-xs text-orange-600">Notifications</p>
                    </div>
                  </div>
                  <div className="text-sm text-orange-700">Manage alerts and notification preferences</div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'facilities' && (
            <div className="space-y-4">
              {filteredFacilities.map((facility) => (
                <div key={facility.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{facility.name}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(facility.status)}`}>
                          {facility.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{facility.location}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Wifi className="w-4 h-4" />
                          {facility.devices} devices
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {facility.users} users
                        </div>
                        <div className="flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          {facility.uptime}% uptime
                        </div>
                        <div className="flex items-center gap-1">
                          <Database className="w-4 h-4" />
                          {facility.dataUsage}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
                        <span>IP: {facility.ipAddress}</span>
                        <span>Last sync: {facility.lastSync}</span>
                        <span>Connection: {facility.connection}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                        <Settings className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {filteredAlerts.map((alert) => (
                <div key={alert.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{alert.facility}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(alert.status)}`}>
                          {alert.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{alert.message}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {alert.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {alert.type}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-800 mb-4">Network Metrics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Bandwidth Usage</span>
                      <span className="text-sm font-medium">78%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Latency</span>
                      <span className="text-sm font-medium">12ms</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Packet Loss</span>
                      <span className="text-sm font-medium">0.1%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-800 mb-4">Security Status</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Firewall Status</span>
                      <span className="text-sm font-medium text-green-600">Active</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">VPN Connections</span>
                      <span className="text-sm font-medium">24</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Security Score</span>
                      <span className="text-sm font-medium">94/100</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ai' && (
            <div className="space-y-6">
              {/* AI Network Optimization Dashboard */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-100 p-8 rounded-2xl border border-purple-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-purple-800 mb-2 flex items-center gap-3">
                      <Brain className="w-6 h-6 text-purple-600" />
                      AI-Powered Network Intelligence
                    </h3>
                    <p className="text-purple-600">Intelligent network optimization and security monitoring</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-green-600 font-medium">AI Active</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{aiNetworkData.optimization.performance}%</div>
                        <div className="text-sm text-gray-600">Performance</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-green-600">90%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${aiNetworkData.optimization.performance}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600">{aiNetworkData.optimization.security}%</div>
                        <div className="text-sm text-gray-600">Security</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-blue-600">95%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full" style={{width: `${aiNetworkData.optimization.security}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-purple-600">{aiNetworkData.optimization.reliability}%</div>
                        <div className="text-sm text-gray-600">Reliability</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-purple-600">99%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full" style={{width: `${aiNetworkData.optimization.reliability}%`}}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl">
                        <Zap className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600">{aiNetworkData.optimization.efficiency}%</div>
                        <div className="text-sm text-gray-600">Efficiency</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Target</span>
                        <span className="text-green-600">90%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{width: `${aiNetworkData.optimization.efficiency}%`}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Recommendations */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800">AI Network Recommendations</h4>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                      {aiNetworkData.recommendations.length} Active
                    </span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  {aiNetworkData.recommendations.map((recommendation) => (
                    <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-semibold text-sm">{recommendation.title}</h5>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              recommendation.priority === 'High' ? 'bg-red-100 text-red-800' :
                              recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {recommendation.priority}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mb-2">{recommendation.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {recommendation.impact}
                            </span>
                            <span className="flex items-center gap-1">
                              <Brain className="w-3 h-3" />
                              {recommendation.aiConfidence}% confidence
                            </span>
                          </div>
                          <div className="mt-2">
                            <span className="text-xs text-blue-600">Facilities: {recommendation.facilities.join(', ')}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Network Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800">Network Health</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium">Excellent</span>
                      </div>
                      <span className="text-sm text-green-600 font-medium">{aiNetworkData.analytics.networkHealth.excellent} facilities</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm font-medium">Good</span>
                      </div>
                      <span className="text-sm text-blue-600 font-medium">{aiNetworkData.analytics.networkHealth.good} facilities</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium">Needs Improvement</span>
                      </div>
                      <span className="text-sm text-red-600 font-medium">{aiNetworkData.analytics.networkHealth.needsImprovement} facilities</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-800">Security Levels</h4>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{aiNetworkData.analytics.securityLevel.high}</div>
                      <div className="text-sm text-gray-600 mb-1">High Security</div>
                      <div className="text-xs text-gray-500">Advanced protection</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{aiNetworkData.analytics.securityLevel.medium}</div>
                      <div className="text-sm text-gray-600 mb-1">Medium Security</div>
                      <div className="text-xs text-gray-500">Standard protection</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{aiNetworkData.analytics.securityLevel.low}</div>
                      <div className="text-sm text-gray-600 mb-1">Low Security</div>
                      <div className="text-xs text-gray-500">Needs attention</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Predictive Insights */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-800">Predictive Insights</h4>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{aiNetworkData.predictions.performanceIssues}</div>
                      <div className="text-sm text-gray-600 mb-1">Performance Issues</div>
                      <div className="text-xs text-gray-500">Expected problems</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{aiNetworkData.predictions.securityThreats}</div>
                      <div className="text-sm text-gray-600 mb-1">Security Threats</div>
                      <div className="text-xs text-gray-500">Potential risks</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{aiNetworkData.predictions.maintenanceNeeds}</div>
                      <div className="text-sm text-gray-600 mb-1">Maintenance Needs</div>
                      <div className="text-xs text-gray-500">Scheduled maintenance</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{aiNetworkData.predictions.optimizationOpportunities}</div>
                      <div className="text-sm text-gray-600 mb-1">Optimization Opportunities</div>
                      <div className="text-xs text-gray-500">Improvement areas</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Add Facility</h3>
              <p className="text-sm text-gray-600">Connect new facility</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold">Security</h3>
              <p className="text-sm text-gray-600">Monitor security</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 h-[140px] flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bell className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">Alerts</h3>
              <p className="text-sm text-gray-600">View notifications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notification System */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`px-4 py-2 rounded-lg shadow-lg text-white ${
              notification.type === 'success' ? 'bg-green-500' :
              notification.type === 'error' ? 'bg-red-500' :
              notification.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
            }`}
          >
            {notification.message}
          </div>
        ))}
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-pink-800 flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-pink-600" />
                Send Secure Message
              </h2>
              <button 
                onClick={() => setShowNewMessageModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Recipient</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500">
                    <option value="">Select family member...</option>
                    <option value="sarah">Sarah Johnson (Daughter)</option>
                    <option value="michael">Michael Wilson (Son)</option>
                    <option value="emma">Emma Davis (Granddaughter)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500">
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input 
                  type="text" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  placeholder="Enter message subject..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 h-32"
                  placeholder="Type your secure message here..."
                ></textarea>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="encrypt" className="rounded" defaultChecked />
                <label htmlFor="encrypt" className="text-sm text-gray-600">End-to-end encryption enabled</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowNewMessageModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('Secure message sent successfully!', 'success');
                    setShowNewMessageModal(false);
                  }}
                  className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Call Modal */}
      {showVideoCallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-blue-800 flex items-center gap-3">
                <Video className="w-6 h-6 text-blue-600" />
                Start Video Call
              </h2>
              <button 
                onClick={() => setShowVideoCallModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Call Recipient</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Select family member...</option>
                    <option value="sarah">Sarah Johnson (Daughter)</option>
                    <option value="michael">Michael Wilson (Son)</option>
                    <option value="emma">Emma Davis (Granddaughter)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Call Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="video">Video Call</option>
                    <option value="audio">Audio Only</option>
                    <option value="group">Group Call</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Call Notes</label>
                <textarea 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24"
                  placeholder="Add any notes for this call..."
                ></textarea>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="record" className="rounded" />
                  <label htmlFor="record" className="text-sm text-gray-600">Record call (with consent)</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="screen" className="rounded" />
                  <label htmlFor="screen" className="text-sm text-gray-600">Enable screen sharing</label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowVideoCallModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('Video call initiated successfully!', 'success');
                    setShowVideoCallModal(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Start Call
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Family Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-purple-800 flex items-center gap-3">
                <UserPlus className="w-6 h-6 text-purple-600" />
                Invite Family Member
              </h2>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter family member's name..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="">Select relationship...</option>
                    <option value="spouse">Spouse</option>
                    <option value="child">Child</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="grandchild">Grandchild</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input 
                    type="email" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter email address..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter phone number..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Resident</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                  <option value="">Select resident...</option>
                  <option value="margaret">Margaret Johnson</option>
                  <option value="robert">Robert Wilson</option>
                  <option value="eleanor">Eleanor Davis</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Access Permissions</label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="messages" className="rounded" defaultChecked />
                    <label htmlFor="messages" className="text-sm text-gray-600">Send Messages</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="calls" className="rounded" defaultChecked />
                    <label htmlFor="calls" className="text-sm text-gray-600">Video Calls</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="photos" className="rounded" defaultChecked />
                    <label htmlFor="photos" className="text-sm text-gray-600">View Photos</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="updates" className="rounded" defaultChecked />
                    <label htmlFor="updates" className="text-sm text-gray-600">Health Updates</label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    showNotification('Family member invitation sent successfully!', 'success');
                    setShowInviteModal(false);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Network;
